import PropTypes from 'prop-types';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Form } from 'antd';
import { CreateUpdateContainer } from '../../../components/CreateUpdateContainer/CreateUpdateContainer';
import { useAuthContext } from '../../../contexts/AuthContext';
import { useFields } from './fields/fields';
import { useErrorMessage } from '../../../utils/errorMessage';
import { useFetchEnums } from '../../../utils/useFetchEnums';
import { createFileFromJSON } from '../../../utils/filesManagement/createFileFromJSON';
import { useOtherInfosFields } from './fields/otherInfosFields';
import { useExtraInfosFields } from './fields/extraInfosFields';
import { useCreateUpdateConfig } from './utils/useCreateUpdateConfig';
import { useSecondaryProfileFields } from './fields/secondaryProfileFields';
import { useSocketContext } from '../../../contexts/SocketContext';
import { EditingLocked } from '../../../components/EditingLocked/EditingLocked';
import { editingLocked } from '../../../utils/editingLocked';

/**
 * CreateUpdateProfile component for creating or updating user profiles.
 * @component
 * @param {Object} props - The properties of the component.
 * @param {string} props.purpose - The purpose of the profile operation, either 'create' or 'update'.
 * @returns {JSX.Element} - React component.
 */
export const CreateUpdateProfile = ({ purpose }) => {
  const { dispatchAPI, user, setUser } = useAuthContext();
  const { message } = useErrorMessage();
  const { t } = useTranslation();
  const { profileLock } = useSocketContext();
  const navigate = useNavigate();
  const { id } = useParams();
  const [form] = Form.useForm();

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const fieldsActiveKey = searchParams.get('fieldsActiveKey');

  const [documentsFileList, setDocumentsFileList] = useState({});
  const [photo, setPhoto] = useState([]);
  const [loading, setLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState();
  const [removedFiles, setRemovedFiles] = useState([]);
  const [country, setCountry] = useState('');
  const [isAnimals, setIsAnimals] = useState(false);

  const { enums, isFieldsLoading } = useFetchEnums('/pensioners/enums', {
    dispatchAPI,
    message
  });
  // Importing and using custom hooks for fields
  const { mainFields } = useFields({
    enums,
    setDocumentsFileList,
    documentsFileList,
    photo,
    setPhoto,
    loading,
    setLoading,
    imageUrl,
    setImageUrl,
    setRemovedFiles,
    form,
    country,
    setCountry
  });

  const { secondaryProfileFields } = useSecondaryProfileFields({
    setDocumentsFileList,
    documentsFileList,
    setRemovedFiles
  });

  const { otherInfosFields } = useOtherInfosFields({
    t,
    setDocumentsFileList,
    documentsFileList,
    setCountry,
    country,
    loading,
    form,
    isAnimals,
    setIsAnimals
  });

  const { extraInfosFields } = useExtraInfosFields({
    t,
    enums,
    isFieldsLoading
  });

  // Mapping fields to an object
  const fieldsActive = {
    mainFields,
    secondaryProfileFields,
    otherInfosFields,
    extraInfosFields
  };
  // Title and icon of the form card for each group of fields
  const fieldsCards = {
    mainFields: { title: t('owners.show.owners_personal_info'), icon: 'user' },
    secondaryProfileFields: {
      title: t('pensioners.form.secondary_pensioner'),
      icon: 'user'
    },
    otherInfosFields: {
      title: t('pensioners.show.additional_info'),
      icon: 'home'
    },
    extraInfosFields: {
      title: t('pensioners.show.extra_info'),
      icon: 'profile'
    }
  };

  // Importing and using custom hook for configuration
  const { config } = useCreateUpdateConfig({
    createFileFromJSON,
    dispatchAPI,
    message,
    setPhoto,
    photo,
    documentsFileList,
    setDocumentsFileList,
    setUser,
    removedFiles,
    setCountry,
    setIsAnimals
  });

  // Setting base URL based on user role
  const baseUrl = `${user.role.split(':')[1].toLowerCase()}s${
    user.role === 'users:PENSIONER' ? '/form/extranet' : `/form/extranet`
  }`;

  return (
    <>
      {editingLocked(profileLock, id) && (
        <EditingLocked
          t={t}
          resources={{
            button: 'my_profile',
            text: 'profile'
          }}
          navigate={navigate}
        />
      )}
      <CreateUpdateContainer
        withPageHeaderCustom
        purpose={purpose}
        baseUrl={baseUrl}
        populate="photo"
        fields={fieldsActive[fieldsActiveKey]}
        fieldsCardTitle={fieldsCards[fieldsActiveKey]?.title}
        fieldsCardIcon={fieldsCards[fieldsActiveKey]?.icon}
        config={config}
        resource="users"
        customFormInstance={form}
        disabled={editingLocked(profileLock, id)}
      />
    </>
  );
};

CreateUpdateProfile.propTypes = {
  purpose: PropTypes.string.isRequired
};
